import { useObserva } from "@morganisbatman/observa-client-react";
import { useEffect, useMemo, useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";

import { radius, space, textStyle } from "@passfirst/ui";

import { authClient } from "./auth-client";
import { type Colors, useColors } from "./theme";
import { trpc } from "./trpc";

interface Providers {
  google: boolean;
  apple: boolean;
}

export function SocialButtons(props: {
  callbackURL?: string;
  // Which page is rendering this — distinguishes signup.submitted vs
  // login.submitted for funnel attribution. The OAuth redirect itself
  // is owned by the provider, so this click is the only signal we
  // have of intent before the user disappears off-app.
  intent: "signup" | "login";
  // Web/wide lays Google + Apple side-by-side (design_handoff_2 §8).
  row?: boolean;
}): JSX.Element | null {
  const observa = useObserva();
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [providers, setProviders] = useState<Providers | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const res = await trpc.config.authProviders.query();
        setProviders({ google: res.google, apple: res.apple });
      } catch {
        setProviders({ google: false, apple: false });
      }
    })();
  }, []);

  if (!providers) return null;
  if (!providers.google && !providers.apple) return null;

  const callbackURL = props.callbackURL ?? "/";
  const { intent, row } = props;
  // Both providers present + row layout → short labels so the 2-up fits.
  const twoUp = Boolean(row) && providers.google && providers.apple;

  async function signInWith(provider: "google" | "apple"): Promise<void> {
    void observa.event({
      eventType:
        intent === "signup" ? "signup.submitted" : "login.submitted",
      payload: { method: provider },
    });
    await authClient.signIn.social({ provider, callbackURL });
  }

  return (
    <View style={styles.stack}>
      <View style={styles.divider}>
        <View style={styles.line} />
        <Text style={styles.dividerText}>or</Text>
        <View style={styles.line} />
      </View>

      <View style={twoUp ? styles.row : styles.stack}>
        {providers.google ? (
          <Pressable
            style={[styles.btn, twoUp && styles.btnFlex]}
            onPress={() => void signInWith("google")}
            accessibilityRole="button"
            accessibilityLabel="Continue with Google"
          >
            <Text style={styles.btnText}>
              {twoUp ? "Google" : "Continue with Google"}
            </Text>
          </Pressable>
        ) : null}

        {providers.apple ? (
          <Pressable
            style={[styles.btn, styles.appleBtn, twoUp && styles.btnFlex]}
            onPress={() => void signInWith("apple")}
            accessibilityRole="button"
            accessibilityLabel="Continue with Apple"
          >
            <Text style={[styles.btnText, styles.appleText]}>
              {twoUp ? "Apple" : "Continue with Apple"}
            </Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
  stack: { gap: space[2], marginTop: space[1] },
  row: { flexDirection: "row", gap: space[2] },
  btnFlex: { flex: 1 },
  divider: {
    flexDirection: "row",
    alignItems: "center",
    gap: space[2],
    paddingVertical: space[1],
  },
  line: { flex: 1, height: 1, backgroundColor: colors.border },
  dividerText: { ...textStyle.caption, fontSize: 12, color: colors.textMuted },
  btn: {
    borderWidth: 1,
    borderColor: colors.border,
    paddingVertical: space[3],
    borderRadius: radius.md,
    alignItems: "center",
    backgroundColor: colors.surface,
  },
  btnText: { ...textStyle.label, fontSize: 15, color: colors.textPrimary },
  // Apple's brand guidelines require a black "Continue with Apple" button.
  // Don't tokenize this — it's a third-party brand affordance, not our palette.
  appleBtn: { backgroundColor: "black", borderColor: "black" },
  appleText: { color: "white" },
  });
}
